import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { bpfAPI } from 'modules/stages';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import type { MutationHookOptions } from '@apollo/client/react/types/types';
import { CreateCreditLineRequestInput, PaymentType, UpdateCreditLineRequestInput } from '@trnsact/trnsact-shared-types';

import { VOApi } from '../api';
import { UpdateVOResponse, UpdateVOVariables } from '../api/types';
import { M_CREATE_CREDIT_LINE_REQUEST, M_UPDATE_CREDIT_LINE_REQUEST } from '../../../queries/common';

export function useVO() {
  const { showNotification } = useNotifications();

  const [onUpdateVO, { loading: isVOUpdating }] = useMutation<UpdateVOResponse, UpdateVOVariables>(VOApi.update);

  const handleUpdateVO = useCallback(
    async (
      variables: UpdateVOVariables,
      fetchOptions?: (api: typeof VOApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        await onUpdateVO({ variables, ...(typeof fetchOptions === 'function' ? fetchOptions(VOApi) : {}) });
      } catch (error) {
        logError(error);
      }
    },
    [onUpdateVO]
  );

  const [onUpdateCurrentStage, { loading: isVOStageUpdating }] = useMutation(VOApi.updateCurrentStage);

  const handleUpdateCurrentStage = useCallback(
    async (
      variables: { voId: string; flowId: string; stageId: string },
      fetchOptions?: (api: typeof VOApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data, errors } = await onUpdateCurrentStage({
          variables: { input: variables },
          ...(typeof fetchOptions === 'function' ? fetchOptions(VOApi) : {}),
        });

        if (!data?.updateVOCurrentStage?.success || errors?.length) {
          showNotification('Something went wrong', { type: 'error' });
        } else {
          showNotification('Stage successfully updated!');
        }
      } catch (error) {
        logError(error);
        showNotification('Something went wrong', { type: 'error' });
      }
    },
    [onUpdateCurrentStage, showNotification]
  );

  const [onCreateCreditLineRequest, { loading: isCreateCreditLineLoading }] = useMutation(M_CREATE_CREDIT_LINE_REQUEST);

  const createCreditLineRequest = useCallback(
    async (data: CreateCreditLineRequestInput) => {
      try {
        const result = await onCreateCreditLineRequest({ variables: { input: data } });
        showNotification('Credit line successfully created!');

        return result;
      } catch (error) {
        logError(error);
        showNotification('Creation of credit line failed!', { type: 'error' });
      }
    },
    [onCreateCreditLineRequest, showNotification]
  );

  const [onUpdateCreditLineRequest, { loading: isUpdateCreditLineLoading }] = useMutation(M_UPDATE_CREDIT_LINE_REQUEST);

  const updateCreditLineRequest = useCallback(
    async (data: UpdateCreditLineRequestInput) => {
      try {
        const result = await onUpdateCreditLineRequest({ variables: { input: data } });
        showNotification('Credit line successfully updated!');

        return result;
      } catch (error) {
        logError(error);
        showNotification('Update of credit line failed!', { type: 'error' });
      }
    },
    [onUpdateCreditLineRequest, showNotification]
  );

  const [onUpdatePaymentType, { loading: isVOPaymentTypeUpdating }] = useMutation(VOApi.updatePaymentType);

  const updateUpdatePaymentType = useCallback(
    async (
      dto: { voId: string; nextPaymentType: PaymentType },
      fetchOptions?: (api: typeof VOApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await onUpdatePaymentType({
          variables: { input: dto },
          ...(typeof fetchOptions === 'function' ? fetchOptions(VOApi) : {}),
        });

        if (!data?.result?.success || data?.result?.error) {
          showNotification(data?.result?.error ?? 'Update Payment type failed!', { type: 'error' });
          return;
        } else {
          showNotification('Payment type successfully updated!');
        }
      } catch (error) {
        logError(error);
        showNotification('Update Payment type failed!', { type: 'error' });
      }
    },
    [onUpdatePaymentType, showNotification]
  );

  const [onUpdateEstimatedCloseDate, { loading: isVOEstimatedCloseDateUpdating }] = useMutation(
    VOApi.updateEstimatedCloseDate
  );

  const updateEstimatedCloseDate = useCallback(
    async (
      dto: { voId: string; estimatedCloseDate: string },
      fetchOptions?: (api: typeof VOApi, bpfApi: typeof bpfAPI) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await onUpdateEstimatedCloseDate({
          variables: { input: dto },
          ...(typeof fetchOptions === 'function' ? fetchOptions(VOApi, bpfAPI) : {}),
        });

        if (!data?.result?.success || data?.result?.error) {
          showNotification(data?.result?.error ?? 'Update Estimated Close Date failed!', { type: 'error' });
          return;
        }

        showNotification('Estimated close date successfully updated!');
      } catch (error) {
        logError(error);
        showNotification('Update Estimated Close Date failed!', { type: 'error' });
      }
    },
    [onUpdateEstimatedCloseDate, showNotification]
  );

  return {
    isVOUpdating,
    handleUpdateVO,
    isVOStageUpdating,
    handleUpdateCurrentStage,
    createCreditLineRequest,
    updateCreditLineRequest,
    updateUpdatePaymentType,
    isVOPaymentTypeUpdating,
    updateEstimatedCloseDate,
    isVOEstimatedCloseDateUpdating,
    isCreateCreditLineLoading,
    isUpdateCreditLineLoading,
  };
}
